import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Share2, Copy, Check } from 'lucide-react';
import type { LinkChallengeInfo } from '@draughts/shared';
import { challengeLinkUrl, shareChallengeLink } from '../../utils/share';
import { variantShortLabel } from '../../utils/variant-labels';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { log } from '../../utils/logger';
import ChallengeDetails from './ChallengeDetails';
import './ChallengeLinkModal.css';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';

interface ChallengeLinkModalProps {
  link: LinkChallengeInfo;
  /** Cancel the link (server-side) and close. */
  onCancel: () => void;
  /** Close without cancelling — the link stays valid in the background. */
  onClose: () => void;
}

/** mm:ss left before the link expires. */
function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * What the challenger sees after creating a shareable link: the URL, the way to
 * send it (native share sheet → WhatsApp on mobile, clipboard elsewhere), and
 * the state of the invite.
 *
 * Closing it does NOT kill the link — that is the normal path, since the whole
 * point is to leave the app and send the message. The game starts by itself
 * (`game:start`) when the friend accepts and both sides are connected.
 */
function ChallengeLinkModal({ link, onCancel, onClose }: ChallengeLinkModalProps) {
  const { t } = useTranslation();
  useOverlayBackButton(true, onClose);
  const riferimentoModale = useModaleAccessibile<HTMLDivElement>(true, onClose);
  const url = challengeLinkUrl(link.id);
  const [feedback, setFeedback] = useState<'copied' | 'failed' | null>(null);
  const [remaining, setRemaining] = useState(() => link.expiresAt - Date.now());

  useEffect(() => {
    const tick = () => setRemaining(link.expiresAt - Date.now());
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [link.expiresAt]);

  useEffect(() => {
    if (!feedback) return;
    const timeout = setTimeout(() => setFeedback(null), 2000);
    return () => clearTimeout(timeout);
  }, [feedback]);

  const claimed = link.status === 'claimed';

  const summary = `${variantShortLabel(t, link.variant)} · ${
    link.timeControl === 'none'
      ? t('game.timeControls.noTimeLimit')
      : link.timeControl.split('-')[1] ?? link.timeControl
  }`;

  const handleShare = async () => {
    const result = await shareChallengeLink(link.id, t, summary);
    log.info('ChallengeLinkModal', 'Share result', { result });
    if (result === 'copied') setFeedback('copied');
    if (result === 'failed') setFeedback('failed');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setFeedback('copied');
    } catch {
      setFeedback('failed');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      {/* Dialogo dichiarato + fuoco governato: vedi `useModaleAccessibile`. */}
      <div
        ref={riferimentoModale}
        className="challenge-link-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="challenge-link-titolo"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="challenge-link-head">
          <h2 id="challenge-link-titolo">{t('online.challenge.link.title')}</h2>
          <button type="button" className="challenge-link-close" aria-label={t('common.close')} onClick={onClose}>
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {claimed ? (
          <p className="challenge-link-claimed">
            {t('online.challenge.link.friendAccepted', { name: link.claimedByUsername ?? '' })}
          </p>
        ) : (
          <p className="challenge-link-intro">{t('online.challenge.link.intro')}</p>
        )}

        <ChallengeDetails
          variant={link.variant}
          timeControl={link.timeControl}
          color={link.color}
          matchType={link.matchType}
          rated={link.rated}
          gameMode={link.gameMode}
          opening={link.opening}
          legs={link.legs}
        />

        {/* The URL is shown in full: on desktop people copy it by hand, and it
            reassures everyone that the link is the plain site address. */}
        <div className="challenge-link-url" title={url}>{url}</div>

        <div className="challenge-link-actions">
          <button type="button" className="challenge-link-share" onClick={handleShare}>
            <Share2 size={18} aria-hidden="true" />
            {t('online.challenge.link.share')}
          </button>
          <button type="button" className="challenge-link-copy" onClick={handleCopy}>
            {feedback === 'copied' ? <Check size={18} aria-hidden="true" /> : <Copy size={18} aria-hidden="true" />}
            {feedback === 'copied' ? t('online.challenge.link.copied') : t('online.challenge.link.copy')}
          </button>
        </div>

        {feedback === 'failed' && (
          <p className="challenge-link-error">{t('online.challenge.link.copyFailed')}</p>
        )}

        <p className="challenge-link-note">
          {claimed
            ? t('online.challenge.link.startingSoon')
            : t('online.challenge.link.expiresIn', { time: formatRemaining(remaining) })}
        </p>

        <button type="button" className="challenge-link-cancel" onClick={onCancel}>
          {t('online.challenge.link.cancelLink')}
        </button>
      </div>
    </div>
  );
}

export default ChallengeLinkModal;
